import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs, deleteDoc, doc, updateDoc, query, orderBy, writeBatch } from 'firebase/firestore';
import { getStorage, ref, deleteObject } from 'firebase/storage';
import { useNavigate } from 'react-router-dom';
import { auth } from '../../config/firebase';
import { signOut } from 'firebase/auth';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy, 
} from '@dnd-kit/sortable';
import AddButton from '../../components/AddButton/AddButton';
import CreateLoyaltyForm from '../../components/CreateLoyaltyForm/CreateLoyaltyForm';
import EditLoyaltyForm from '../../components/EditLoyaltyForm/EditLoyaltyForm';
import { SortableLoyaltyCard } from './SortableLoyaltyCard';
import './Loyalty.css';

const Loyalty = () => {
  const navigate = useNavigate();
  const [loyaltyItems, setLoyaltyItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [itemToEdit, setItemToEdit] = useState(null);
  
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('loyaltyViewMode') || 'grid';
  });
  
  const [sortOrderDate, setSortOrderDate] = useState(() => { 
    return localStorage.getItem('loyaltySortOrderDate') || 'desc';
  });

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDeleteAsState, setItemToDeleteAsState] = useState(null); 
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    localStorage.setItem('loyaltyViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('loyaltySortOrderDate', sortOrderDate);
  }, [sortOrderDate]);

  const fetchLoyaltyItems = async () => {
    try {
      setLoading(true);
      const db = getFirestore();
      const itemsRef = collection(db, 'fidelite');
      const q = query(itemsRef, orderBy('order', 'asc')); 
      const snapshot = await getDocs(q);
      const itemsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setLoyaltyItems(itemsList);
    } catch (error) {
      console.error("Erreur lors de la récupération des éléments de fidélité:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLoyaltyItems();
  }, []);

  const initializeOrder = async () => {
    try {
      const db = getFirestore();
      const itemsRef = collection(db, 'fidelite');
      const snapshot = await getDocs(itemsRef);
      const sortedDocs = snapshot.docs.sort((a, b) => {
        const dateA = a.data().dateCreation ? new Date(a.data().dateCreation) : new Date(0);
        const dateB = b.data().dateCreation ? new Date(b.data().dateCreation) : new Date(0);
        return dateB - dateA; 
      });
      const batch = writeBatch(db);
      sortedDocs.forEach((itemDoc, index) => {
        const itemRef = doc(db, 'fidelite', itemDoc.id);
        batch.update(itemRef, { order: index });
      });
      await batch.commit();
      await fetchLoyaltyItems(); 
    } catch (error) {
      console.error("Erreur lors de l'initialisation de l'ordre des avantages:", error);
    }
  };

  useEffect(() => {
    const initializeLoyaltyOrderOnce = async () => {
      const hasInitialized = localStorage.getItem('loyaltyOrderInitialized');
      if (!hasInitialized) {
        const { writeBatch: firestoreWriteBatch, getDocs: firestoreGetDocs } = await import('firebase/firestore');
        const db = getFirestore();
        const itemsRef = collection(db, 'fidelite');
        const snapshot = await firestoreGetDocs(itemsRef);
        const sortedDocs = snapshot.docs.sort((a, b) => {
          const dateA = a.data().dateCreation ? new Date(a.data().dateCreation) : new Date(0);
          const dateB = b.data().dateCreation ? new Date(b.data().dateCreation) : new Date(0);
          return dateB - dateA; 
        });
        const batch = firestoreWriteBatch(db);
        sortedDocs.forEach((itemDoc, index) => {
          const itemRef = doc(db, 'fidelite', itemDoc.id);
          batch.update(itemRef, { order: index });
        });
        await batch.commit();
        localStorage.setItem('loyaltyOrderInitialized', 'true');
        await fetchLoyaltyItems();
      }
    };
    initializeLoyaltyOrderOnce();
  }, []);

  const handleDeleteClick = (item) => {
    setItemToDeleteAsState(item); 
    setShowDeleteConfirm(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDeleteAsState) return; 
    setIsDeleting(true);
    try {
      const db = getFirestore();
      const storage = getStorage();
      if (itemToDeleteAsState.imageUrl) { 
        const imageRef = ref(storage, itemToDeleteAsState.imageUrl); 
        await deleteObject(imageRef).catch(error => {
          console.log("L'image n'existe peut-être plus dans le storage:", error);
        });
      }
      await deleteDoc(doc(db, 'fidelite', itemToDeleteAsState.id)); 
      await fetchLoyaltyItems(); 
      setShowDeleteConfirm(false);
      setItemToDeleteAsState(null); 
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'avantage:', error);
      alert('Une erreur est survenue lors de la suppression');
    } finally {
      setIsDeleting(false);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setItemToDeleteAsState(null); 
  };

  const handleEditClick = (item) => {
    setItemToEdit(item);
    setShowEditForm(true);
  };
  
  const handleEditSuccess = async () => {
    setShowEditForm(false);
    setItemToEdit(null);
    await fetchLoyaltyItems();
  };
  
  const handleCreateSuccess = async () => {
    setShowCreateForm(false);
    await fetchLoyaltyItems();
  };

  const sortedLoyaltyItems = [...loyaltyItems].sort((a, b) => {
    const orderA = typeof a.order === 'number' ? a.order : Infinity;
    const orderB = typeof b.order === 'number' ? b.order : Infinity;
    return orderA - orderB;
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setLoyaltyItems((currentItems) => {
        const oldIndex = currentItems.findIndex((item) => item.id === active.id);
        const newIndex = currentItems.findIndex((item) => item.id === over.id);
        if (oldIndex === -1 || newIndex === -1) return currentItems;
        let movedItems = arrayMove(currentItems, oldIndex, newIndex);
        movedItems = movedItems.map((item, index) => ({ ...item, order: index }));
        const db = getFirestore();
        movedItems.forEach(async (item) => {
          const itemRef = doc(db, 'fidelite', item.id);
          await updateDoc(itemRef, { order: item.order });
        });
        return movedItems;
      });
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    }
  };

  return (
    <div className="loyalty">
      <div className="loyalty-page-content"> 
        <div className="dashboard-container">
          <main className="main-content">
            <div className="content-header">
              <h1>Avantages</h1>
              <div className="header-actions">
                <div className="sort-filter">
                  <select
                    value={sortOrderDate}
                    onChange={(e) => setSortOrderDate(e.target.value)}
                    className="sort-select"
                  >
                    <option value="desc">Plus récent (Date)</option>
                    <option value="asc">Plus ancien (Date)</option>
                  </select>
                </div>
                <div className="view-toggle">
                  <button 
                    className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                  >
                    <i className="fas fa-th-large"></i>
                  </button>
                  <button 
                    className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                  >
                    <i className="fas fa-list"></i>
                  </button>
                </div>
                <div className="button-container">
                  <AddButton onClick={() => setShowCreateForm(true)} />
                </div>
              </div>
            </div>

            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={sortedLoyaltyItems.map(item => item.id)}
                strategy={viewMode === 'grid' ? rectSortingStrategy : verticalListSortingStrategy}
              >
                <div className={`loyalty-container ${viewMode}`}>
                  {loading ? (
                    <div className="loading">Chargement...</div>
                  ) : sortedLoyaltyItems.length === 0 ? (
                    <div className="no-results">Aucun avantage pour le moment.</div>
                  ) : (
                    sortedLoyaltyItems.map(item => (
                      <SortableLoyaltyCard
                        key={item.id}
                        id={item.id}
                        item={item}
                        viewMode={viewMode}
                        onEdit={handleEditClick}
                        onDelete={handleDeleteClick} 
                        isDeleting={isDeleting && itemToDeleteAsState?.id === item.id} 
                        itemToDelete={itemToDeleteAsState} 
                      />
                    ))
                  )}
                </div>
              </SortableContext>
            </DndContext>

            {showCreateForm && (
              <CreateLoyaltyForm 
                onClose={() => setShowCreateForm(false)}
                onSuccess={handleCreateSuccess}
              />
            )}

            {showDeleteConfirm && itemToDeleteAsState && ( 
              <div className="modal-overlay">
                <div className="confirm-modal">
                  <h3>Confirmer la suppression</h3>
                  <p>Êtes-vous sûr de vouloir supprimer "{itemToDeleteAsState.titre}" ?</p>
                  <p className="warning-text">Cette action est irréversible.</p>
                  <div className="confirm-actions">
                    <button 
                      className="cancel-btn"
                      onClick={handleDeleteCancel}
                      disabled={isDeleting}
                    >
                      Annuler
                    </button>
                    <button 
                      className="delete-btn"
                      onClick={handleDeleteConfirm}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Suppression...' : 'Supprimer'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showEditForm && itemToEdit && (
              <EditLoyaltyForm 
                item={itemToEdit}
                onClose={() => {
                  setShowEditForm(false);
                  setItemToEdit(null);
                }}
                onSuccess={handleEditSuccess}
              />
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default Loyalty; 